import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import uniqid from 'uniqid';
import { useQueryClient } from '@tanstack/react-query';

import { useCurrentUser } from 'hooks/api/use-current-user';
import { conversationUsageQueryKey } from 'hooks/use-conversation-usage';

import {
  trackMarkChatStarted,
  trackMarkResponseFailed,
  trackMarkResponseReceived,
} from '../analytics/mark-panel-analytics';
import type {
  MarkChatAnalyticsSurface,
  MarkPanelResponseRequestType,
} from '../analytics/mark-panel-analytics.constants';

import { useApiCreateConversation } from './use-api-create-conversation';
import { useApiGetConversationMessages } from './use-api-get-conversation-messages';
import { useApiCreateConversationMessage } from './use-api-create-conversation-message';
import { useApiRegenerateMessage } from './use-api-regenerate-message';
import { useTokenLimit } from './use-token-limit';
import useChatMessageInterceptor from './use-chat-message-interceptor';
import useActiveInputPart from './use-active-input-part';
import type { ActiveInputPart } from './use-active-input-part';
import useInputPartLifecycle from './use-input-part-lifecycle';

import { useAiAssistantSession } from '../context/ai-assistant-session-context';

import {
  resolveMessageParts,
  updateMessageById,
} from '../helpers/message-helpers';
import {
  clearPendingStream,
  getPendingStream,
  isLikelyPageUnloadError,
  setPendingStream,
  shouldPollForPendingStream,
  truncateToPendingTarget,
  withPendingLoadingState,
} from '../helpers/pending-stream-helpers';

import { AI_ASSISTANT_CONVERSATIONS_KEY } from '../ai-assistant.constants';

import type {
  AiChatMessage,
  MarkPromptSource,
  StreamChunk,
} from '../ai-chat.types';
import type { MarkContextKind } from '../mark-panel/mark-panel.types';

export interface UseMarkChatArgs {
  conversationId: string | null;
  clientId: string | number;
  isReadOnly?: boolean;
  onConversationCreated: (conversationId: string) => void;
  analyticsSurface: MarkChatAnalyticsSurface;
  analyticsContext?: { contextKind: MarkContextKind; contextSection: string };
  buildMessageRequest?: (requestMessage: string) => Record<string, unknown>;
}

export interface EditingMessage {
  id: string;
  content: string;
}

export interface UseMarkChatResult {
  oktaUserId: string;
  messages: AiChatMessage[];
  isLoadingConversation: boolean;
  isInputDisabled: boolean;
  isBlocked: boolean;
  limitReachedMessage: string;
  activeInputPart: ActiveInputPart | null;
  sendMessage: (text: string, source?: MarkPromptSource) => void;
  retryMessage: (messageId: string, originalRequest?: string) => Promise<void>;
  regenerateMessage: (messageId: string) => Promise<void>;
  editingMessage: EditingMessage | null;
  startEditingMessage: (messageId: string) => void;
  cancelEditingMessage: () => void;
  submitEditedMessage: (content: string) => Promise<void>;
  submitInputPart: (text: string) => void;
  dismissInputPart: () => void;
  messagesEndRef: RefObject<HTMLDivElement | null>;
}

const defaultBuildMessageRequest = (
  requestMessage: string
): Record<string, unknown> => ({ requestMessage });

export const useMarkChat = ({
  conversationId,
  clientId,
  isReadOnly,
  onConversationCreated,
  analyticsSurface,
  analyticsContext,
  buildMessageRequest = defaultBuildMessageRequest,
}: UseMarkChatArgs): UseMarkChatResult => {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    isBlocked,
    limitReachedMessage,
    isUsageLimitError,
    resetLimitFlag,
    registerLimitError,
  } = useTokenLimit(currentUser?.data);

  const {
    getMessagesForConversation,
    setMessagesForConversation,
    getStreamingMessageId,
    setStreamingForConversation,
  } = useAiAssistantSession();

  const [isRecoveringPending, setIsRecoveringPending] = useState(false);
  const recoveryCheckedConversationIdRef = useRef<string | null>(null);
  const [editingMessage, setEditingMessage] = useState<EditingMessage | null>(
    null
  );

  useEffect(() => {
    setEditingMessage(null);
  }, [conversationId]);

  const { data: conversationMessages = [], isFetching: isLoadingConversation } =
    useApiGetConversationMessages({
      oktaUserId: currentUser?.data?.oktaUserId as string,
      conversationId,
      clientId,
      refetchWhilePending: isRecoveringPending,
    });

  const messages = getMessagesForConversation(
    conversationId ?? ''
  ) as AiChatMessage[];

  // After a full page refresh, local session state is empty but sessionStorage may
  // still mark an in-flight send/regenerate — enter recovery (poll + loading UI).
  // Track the conversation id so hydrating a loading placeholder does not re-enter
  // this effect and exit recovery early.
  useEffect(() => {
    if (!conversationId) {
      recoveryCheckedConversationIdRef.current = null;
      setIsRecoveringPending(false);

      return;
    }

    if (recoveryCheckedConversationIdRef.current === conversationId) {
      return;
    }

    recoveryCheckedConversationIdRef.current = conversationId;

    const pending = getPendingStream(conversationId);
    const hasLocalMessages =
      getMessagesForConversation(conversationId).length > 0;

    setIsRecoveringPending(Boolean(pending) && !hasLocalMessages);
  }, [conversationId, getMessagesForConversation]);

  useEffect(() => {
    if (!conversationId || isLoadingConversation) return;

    const serverMessages = conversationMessages as AiChatMessage[];

    if (isRecoveringPending) {
      const pending = getPendingStream(conversationId);

      if (shouldPollForPendingStream(serverMessages, pending)) {
        setMessagesForConversation(conversationId, () =>
          withPendingLoadingState(serverMessages, pending)
        );
        setStreamingForConversation(
          conversationId,
          pending?.type === 'regenerate' || pending?.type === 'edit'
            ? pending.messageId
            : `pending-stream-${conversationId}`
        );

        return;
      }

      clearPendingStream(conversationId);
      setIsRecoveringPending(false);
      setStreamingForConversation(conversationId, null);

      if (serverMessages.length > 0) {
        setMessagesForConversation(conversationId, () =>
          truncateToPendingTarget(serverMessages, pending)
        );
      }

      return;
    }

    if (messages.length === 0 && serverMessages.length > 0) {
      setMessagesForConversation(conversationId, () => serverMessages);
    }
  }, [
    conversationId,
    isLoadingConversation,
    conversationMessages,
    isRecoveringPending,
    messages.length,
    setMessagesForConversation,
    setStreamingForConversation,
  ]);

  const { mutateAsync: createConversationMessage } =
    useApiCreateConversationMessage({
      oktaUserId: currentUser?.data?.oktaUserId as string,
      clientId,
    });

  const { mutateAsync: createConversation, isPending: isCreatingConversation } =
    useApiCreateConversation({
      oktaUserId: currentUser?.data?.oktaUserId as string,
      clientId,
    });

  const { mutateAsync: regenerateMessage } = useApiRegenerateMessage({
    oktaUserId: currentUser?.data?.oktaUserId as string,
    conversationId: conversationId ?? '',
    clientId,
  });

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({ block: 'nearest' });
  };

  useEffect(() => {
    if (messages.length) scrollToBottom();
  }, [messages]);

  const activeInputPart = useActiveInputPart(messages);

  const hasActiveInputPart = activeInputPart != null;

  useEffect(() => {
    if (hasActiveInputPart) {
      setEditingMessage(null);
    }
  }, [hasActiveInputPart]);

  const { dismiss: dismissInputPart, submitAndSend: submitInputPart } =
    useInputPartLifecycle({
      activeMessage: activeInputPart?.message ?? null,
      conversationId,
      messages,
      setMessagesForConversation,
      sendMessage: (text: string) => {
        if (isBlocked) return;

        handleSendMessage(text, false, 'guided');
      },
    });

  const { interceptSendMessage } = useChatMessageInterceptor({
    conversationId,
    setMessagesForConversation,
    originalSendMessage: (requestMessage, isRetrying, source) => {
      void handleSendMessage(requestMessage, isRetrying, source);
    },
  });

  const invalidateTokenUsage = (): void => {
    const oktaUserId = currentUser?.data?.oktaUserId;

    if (oktaUserId) {
      queryClient.invalidateQueries({
        queryKey: conversationUsageQueryKey(oktaUserId),
      });
    }
  };

  // Constellation orders sessions by recency and titles a new one from its first
  // message, so the sidebar is stale on both counts once a stream settles — the
  // conversation just written to has moved to the top of the list.
  const invalidateConversations = (): void => {
    queryClient.invalidateQueries({
      queryKey: [AI_ASSISTANT_CONVERSATIONS_KEY],
    });
  };

  const createOnChunk =
    (
      targetConversationId: string,
      targetMessageId: string,
      requestType: MarkPanelResponseRequestType
    ) =>
    (chunk: unknown): void => {
      const typedChunk = (chunk ?? {}) as StreamChunk;

      if (!chunk || Object.keys(typedChunk).length === 0) return;

      if (typedChunk.stage === 'completed') {
        const parts = resolveMessageParts(typedChunk);

        clearPendingStream(targetConversationId);
        setStreamingForConversation(targetConversationId, null);
        setMessagesForConversation(targetConversationId, (prev) =>
          prev.map((message) =>
            message.id !== targetMessageId
              ? message
              : {
                  ...message,
                  id: typedChunk.messageId ?? message.id,
                  content: typedChunk.content ?? '',
                  parts,
                  inputPartDismissed: false,
                  loading: false,
                  error: false,
                  limitError: false,
                  originalRequest: undefined,
                }
          )
        );
        trackMarkResponseReceived({ surface: analyticsSurface, requestType });

        return;
      }

      setMessagesForConversation(targetConversationId, (prev) =>
        prev.map((message) =>
          message.id !== targetMessageId
            ? message
            : {
                ...message,
                content: typedChunk.content,
                loading: true,
                error: false,
                limitError: false,
              }
        )
      );
    };

  const setMessageError = (
    targetConversationId: string,
    messageId: string,
    {
      content,
      originalRequest,
      limitError = false,
    }: { content: string; originalRequest?: string; limitError?: boolean }
  ): void => {
    clearPendingStream(targetConversationId);
    setStreamingForConversation(targetConversationId, null);
    setMessagesForConversation(targetConversationId, (previousMessages) =>
      updateMessageById(previousMessages, messageId, {
        content,
        error: true,
        limitError,
        loading: false,
        ...(originalRequest != null && { originalRequest }),
      })
    );
  };

  const handleSendFailure = (
    targetConversationId: string,
    messageId: string,
    error: unknown,
    {
      fallbackContent,
      originalRequest,
    }: { fallbackContent: string; originalRequest?: string },
    requestType: MarkPanelResponseRequestType
  ): void => {
    if (isUsageLimitError(error)) {
      registerLimitError();
      setMessageError(targetConversationId, messageId, {
        content: limitReachedMessage,
        limitError: true,
      });
      trackMarkResponseFailed({
        surface: analyticsSurface,
        requestType,
        errorType: 'usageLimit',
      });

      return;
    }

    setMessageError(targetConversationId, messageId, {
      content: fallbackContent,
      ...(originalRequest != null && { originalRequest }),
    });
    trackMarkResponseFailed({
      surface: analyticsSurface,
      requestType,
      errorType: 'generic',
    });
  };

  async function handleSendMessage(
    requestMessage: string | unknown,
    isRetrying = false,
    source?: MarkPromptSource
  ): Promise<void> {
    if (isBlocked) return;

    setEditingMessage(null);

    let activeConversationId = conversationId;
    const isFirstMessage = !messages.length;
    const requestType: MarkPanelResponseRequestType = isRetrying
      ? 'retry'
      : 'send';

    if (isFirstMessage && !isRetrying) {
      trackMarkChatStarted({
        surface: analyticsSurface,
        promptType: source ?? 'custom',
        ...(analyticsContext && {
          contextKind: analyticsContext.contextKind,
          contextSection: analyticsContext.contextSection,
        }),
      });
    }

    if (isFirstMessage) {
      await createConversation({
        clientId: currentUser?.data?.clientId,
      }).then((conversation) => {
        const typedConversation = conversation as { id: string };

        activeConversationId = typedConversation.id;
        onConversationCreated(activeConversationId);
      });
    }

    const userMessage: AiChatMessage = {
      id: uniqid(),
      content: requestMessage as string,
      isUser: true,
    };

    const aiMessageId = uniqid();

    if (!isRetrying && activeConversationId) {
      setMessagesForConversation(activeConversationId, (prev) => [
        ...prev,
        userMessage,
      ]);
    }

    if (activeConversationId) {
      setMessagesForConversation(activeConversationId, (prev) => [
        ...prev,
        {
          id: aiMessageId,
          content: 'Generating response...',
          isUser: false,
          loading: true,
        } as AiChatMessage,
      ]);

      setStreamingForConversation(activeConversationId, aiMessageId);
      setPendingStream({
        type: 'send',
        conversationId: activeConversationId,
        startedAt: Date.now(),
      });
    }

    resetLimitFlag();

    try {
      if (activeConversationId) {
        const streamResult = await createConversationMessage({
          conversationId: activeConversationId,
          data: buildMessageRequest(requestMessage as string),
          onChunk: createOnChunk(
            activeConversationId,
            aiMessageId,
            requestType
          ),
        });

        // Keep the pending marker when the browser aborts the stream (refresh) so
        // recovery polling can pick up the Constellation reply after reload.
        if (!streamResult.aborted) {
          clearPendingStream(activeConversationId);
        }
      }
    } catch (error) {
      if (activeConversationId && !isLikelyPageUnloadError(error)) {
        handleSendFailure(
          activeConversationId,
          aiMessageId,
          error,
          {
            fallbackContent:
              'Something went wrong while generating your response. Please try again.',
            originalRequest: requestMessage as string,
          },
          requestType
        );
      }
    } finally {
      invalidateTokenUsage();
      // Also on the failure path: the user message is persisted upstream before
      // the reply streams, so the reorder happens even when generation fails.
      invalidateConversations();
    }
  }

  const handleRetry = async (
    messageId: string,
    originalRequest?: string
  ): Promise<void> => {
    if (isBlocked || !conversationId) return;
    setMessagesForConversation(conversationId, (prev) =>
      prev.filter((message) => message.id !== messageId)
    );
    await handleSendMessage(originalRequest, true);
  };

  const streamReplyRegeneration = async ({
    targetConversationId,
    aiMessageId,
    aiMessageIndex,
    userMessageEdit,
    fallbackContent,
  }: {
    targetConversationId: string;
    aiMessageId: string;
    aiMessageIndex: number;
    userMessageEdit?: { id: string; content: string; originalContent: string };
    fallbackContent: string;
  }): Promise<void> => {
    const requestType = userMessageEdit ? 'edit' : 'regenerate';
    const contentSnapshot = messages[aiMessageIndex]?.content ?? '';

    setMessagesForConversation(targetConversationId, (prev) =>
      prev.slice(0, aiMessageIndex + 1).map((message) => {
        if (message.id === aiMessageId) {
          return {
            ...message,
            content: 'Generating response...',
            loading: true,
            error: false,
            limitError: false,
            originalRequest: undefined,
          };
        }

        if (message.id === userMessageEdit?.id) {
          return { ...message, content: userMessageEdit.content };
        }

        return message;
      })
    );

    setStreamingForConversation(targetConversationId, aiMessageId);
    setPendingStream({
      type: requestType,
      conversationId: targetConversationId,
      messageId: aiMessageId,
      messageIndex: aiMessageIndex,
      contentSnapshot,
      startedAt: Date.now(),
    });
    resetLimitFlag();

    try {
      const streamResult = await regenerateMessage({
        messageId: aiMessageId,
        content: userMessageEdit?.content,
        onChunk: createOnChunk(targetConversationId, aiMessageId, requestType),
      });

      if (!streamResult.aborted) {
        clearPendingStream(targetConversationId);
      }
    } catch (error) {
      if (!isLikelyPageUnloadError(error)) {
        handleSendFailure(
          targetConversationId,
          aiMessageId,
          error,
          { fallbackContent },
          requestType
        );

        if (userMessageEdit) {
          setMessagesForConversation(targetConversationId, (prev) =>
            prev.map((message) =>
              message.id === userMessageEdit.id
                ? { ...message, content: userMessageEdit.originalContent }
                : message
            )
          );
        }
      }
    } finally {
      invalidateTokenUsage();
      invalidateConversations();
    }
  };

  const handleRegenerateMessage = async (messageId: string): Promise<void> => {
    if (isBlocked) return;

    setEditingMessage(null);

    const index = messages.findIndex((message) => message.id === messageId);

    if (index === -1 || !conversationId) return;

    await streamReplyRegeneration({
      targetConversationId: conversationId,
      aiMessageId: messageId,
      aiMessageIndex: index,
      fallbackContent:
        'Something went wrong while regenerating. Please try again.',
    });
  };

  const startEditingMessage = (messageId: string): void => {
    if (isBlocked || !conversationId) return;

    if (getStreamingMessageId(conversationId) != null) return;

    const index = messages.findIndex((message) => message.id === messageId);

    if (index === -1) return;

    const target = messages[index];
    // Constellation's retry endpoint only accepts an assistant message id, and can only
    // replay against a reply that has already completed — see submitEditedMessage.
    const aiReply = messages[index + 1];

    if (!aiReply || aiReply.isUser || aiReply.loading) return;

    setEditingMessage({ id: messageId, content: target.content ?? '' });
  };

  const cancelEditingMessage = (): void => {
    setEditingMessage(null);
  };

  const handleEditMessage = async (content: string): Promise<void> => {
    const target = editingMessage;

    setEditingMessage(null);

    if (!target || isBlocked || !conversationId) return;

    const index = messages.findIndex((message) => message.id === target.id);
    const aiReply = index === -1 ? undefined : messages[index + 1];

    if (!aiReply || aiReply.isUser) return;

    dismissInputPart();

    await streamReplyRegeneration({
      targetConversationId: conversationId,
      aiMessageId: aiReply.id,
      aiMessageIndex: index + 1,
      userMessageEdit: {
        id: target.id,
        content,
        originalContent: target.content ?? '',
      },
      fallbackContent:
        'Something went wrong while sending your edited message. Please try again.',
    });
  };

  const isInputDisabled =
    isLoadingConversation ||
    isCreatingConversation ||
    getStreamingMessageId(conversationId ?? '') != null ||
    Boolean(isReadOnly);

  const sendMessage = useCallback(
    (text: string, source?: MarkPromptSource) => {
      if (isBlocked) return;

      interceptSendMessage(text, false, source);
    },
    [isBlocked, interceptSendMessage]
  );

  return {
    oktaUserId: currentUser?.data?.oktaUserId as string,
    messages,
    isLoadingConversation,
    isInputDisabled,
    isBlocked,
    limitReachedMessage,
    activeInputPart: editingMessage ? null : activeInputPart,
    sendMessage,
    retryMessage: handleRetry,
    regenerateMessage: handleRegenerateMessage,
    editingMessage,
    startEditingMessage,
    cancelEditingMessage,
    submitEditedMessage: handleEditMessage,
    submitInputPart,
    dismissInputPart,
    messagesEndRef,
  };
};
